import { InfoIcon } from "@/icons";
import { useEffect, useState } from "react";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { listInventoryPokemons } from "@/services/inventory/list-inventory-pokemons";
import { InventoryPokemonsOutput, PokemonBoxData } from "types/web-app";
import { useAuth } from "@/providers/auth-provider";
import { Alert } from "@/components/alert/alert";
import { getBoxes } from "@/services/box/get-boxes";
import { InventoryList } from "./inventory-list";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import cn from "classnames";
import { InventoryItemsView } from "../inventory-items/inventory-items-view";
import { PacksView } from "../packs/packs-view";

type CollectionTab = "pokes" | "items" | "packs" | "dex";

const TABS: { id: CollectionTab; label: string }[] = [
  { id: "pokes", label: "Pokes" },
  { id: "items", label: "Items" },
  { id: "packs", label: "Packs" },
  { id: "dex", label: "Dex" },
];

export const InventoryPokeView = () => {
  const { intent, selectedPokemons, setIntent, setCurrentRoute } = useSPAContext();
  const { getAccessToken } = useAuth();

  const [activeTab, setActiveTab] = useState<CollectionTab>("pokes");

  const [boxes, setBoxes] = useState<PokemonBoxData[]>([]);

  const [pokemons, setPokemons] = useState<
    InventoryPokemonsOutput["player_pokemons"] | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (intent) {
      setActiveTab("pokes");
    }
  }, [intent]);

  const getPokemons = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!getAccessToken()) {
        setError("Authentication failed. Please try logging in again.");
        return;
      }

      const response = await listInventoryPokemons();
      setPokemons(response.data?.player_pokemons ?? []);
    } catch (err) {
      console.error("Failed to fetch pokemons:", err);
      setError("Failed to load your pokemons. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBoxes = async () => {
    if (!getAccessToken()) return;

    try {
      const boxesRes = await getBoxes();
      if (boxesRes.ok) setBoxes(boxesRes.data.boxes);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    void getPokemons();
    void fetchBoxes();
  }, []);

  const handleRefresh = async () => {
    await Promise.all([getPokemons(), fetchBoxes()]);
  };

  const handleDoneSelectingPokesToTrain = () => {
    if (selectedPokemons.length !== 2) {
      Alert("You must select 2 Pokémon to train");
      return;
    }

    setIntent(undefined);
    setCurrentRoute(RouteNames.TRAINING_FACILITY);
  };

  return (
    <div className="flex flex-col gap-2 w-full h-full pb-2 pt-2 bg-black/50 backdrop-blur-[5px] overflow-hidden">
      {/* Intent Headers */}
      {intent === Intent.SELECTING_POKEMON_FOR_MAIN_DECK && (
        <div className="bg-pk-orange-2 w-full flex flex-row items-center ">
          <div className="flex items-center gap-4 w-full z-10 px-4 py-2 bg-pk-orange-2/50">
            <InfoIcon width={24} height={24} stroke="#000000" strokeWidth={3} />
            <p className="text-black font-medium text-xs xs:text-sm">
              Select Pokémon for Main Deck
            </p>
            <button
              className="bg-pk-orange-5 text-pk-white px-2 xs:px-4 py-1 rounded-md z-30 ml-auto shadow-cartoon-sm"
              onClick={() => setIntent(undefined)}
            >
              <span className="text-xs xs:text-sm">Cancel</span>
            </button>
          </div>
        </div>
      )}

      {intent === Intent.SELECTING_POKEMONS_TO_TRAIN && (
        <div className="z-20 w-full border-b-4 border-b-pk-blue-2 flex flex-row items-center ">
          <div className="flex items-center gap-4 w-full z-10 p-2 bg-pk-blue-5">
            <InfoIcon width={24} height={24} stroke="#1f3a5f" strokeWidth={3} />
            <p className="text-black font-medium text-xs xs:text-sm">
              Select 2 Pokémon to Train
            </p>
            <SimpleButton
              variant="blue"
              onClick={handleDoneSelectingPokesToTrain}
              className="ml-auto"
            >
              Done
            </SimpleButton>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col px-2 gap-2 flex-1 min-h-0 overflow-hidden">
        {/* Tab Header */}
        <StandardFrame variant="purple" className="">
          <div className="grid grid-cols-4 items-center justify-between">
            {TABS.map((tab) => (
              <div
                key={tab.id}
                className="flex justify-center items-center gap-2"
                style={{
                  opacity: activeTab === tab.id ? 1 : 0.5,
                  backgroundColor:
                    activeTab === tab.id
                      ? "rgba(255, 255, 255, 0.51)"
                      : "transparent",
                }}
              >
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "text-base xs:text-lg font-black uppercase tracking-wide drop-shadow-md transition-all py-1",
                    activeTab === tab.id
                      ? "text-white"
                      : "text-white/40 hover:text-white/60",
                  )}
                >
                  {tab.label}
                </button>
              </div>
            ))}
          </div>
        </StandardFrame>

        {/* Content */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          {activeTab === "pokes" && (
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              <InventoryList
                pokemons={pokemons}
                isLoading={isLoading}
                error={error}
                onRefresh={handleRefresh}
                boxes={boxes}
              />
            </div>
          )}
          {activeTab === "items" && <InventoryItemsView embedded />}
          {activeTab === "packs" && <PacksView embedded />}
          {activeTab === "dex" && <CollectionListView />}
        </div>
      </div>
    </div>
  );
};
